import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { usePathname } from 'next/navigation';
import { ThemeProvider } from '@mui/material/styles';
import { useCompanyContext } from '@/app/context/CompanyContext';
import { useUserContext } from '@/app/context/UserContext';
import { createAppTheme } from '@/config/mui-theme.config';
import { applyHtmlDarkClass, getStoredColorMode, isAuthMarketingDarkRoute, setStoredColorMode, shouldApplyDark } from '@/utils/color-mode.util';
const ColorModeContext = createContext(null);
export function ColorModeProvider({
  children,
  fontFamily
}) {
  const pathname = usePathname();
  const {
    hasNoUser,
    companyDetails
  } = useUserContext();
  const {
    companyConfig
  } = useCompanyContext();
  // Prefer user-data companyDetails (available on public routes via companySlug); fall back to CompanyContext.
  const isDarkModeFeatureEnabled = companyDetails?.isDarkModeEnabled === true || companyConfig?.isDarkModeEnabled === true;
  const allowDark = isAuthMarketingDarkRoute(pathname) || isDarkModeFeatureEnabled;
  const [colorMode, setColorModeState] = useState('light');
  const [preferenceReady, setPreferenceReady] = useState(false);
  useLayoutEffect(() => {
    setColorModeState(getStoredColorMode());
    setPreferenceReady(true);
  }, []);
  const isDark = preferenceReady && allowDark && shouldApplyDark({
    preference: colorMode,
    pathname,
    hasNoUser
  });
  useLayoutEffect(() => {
    applyHtmlDarkClass(isDark);
  }, [isDark]);

  // Crossfade the whole page between themes (styles: `::view-transition-*(root)` in globals.css) instead of every
  // surface flipping at once. flushSync commits the new theme — and the `dark` class, set in a layout effect —
  // inside the callback, so the transition captures the finished page. Instant without the API or under reduced motion.
  const setColorMode = useCallback(mode => {
    setStoredColorMode(mode);
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || typeof document.startViewTransition !== 'function') {
      setColorModeState(mode);
      return;
    }
    document.startViewTransition(() => {
      flushSync(() => setColorModeState(mode));
    });
  }, []);
  const toggleColorMode = useCallback(() => {
    setColorMode(colorMode === 'dark' ? 'light' : 'dark');
  }, [colorMode, setColorMode]);

  // Recreate when createAppTheme identity changes (HMR / theme config edits).
  const theme = useMemo(() => createAppTheme(isDark ? 'dark' : 'light', fontFamily), [isDark, fontFamily, createAppTheme]);
  const value = useMemo(() => ({
    colorMode,
    setColorMode,
    toggleColorMode,
    isDark
  }), [colorMode, setColorMode, toggleColorMode, isDark]);
  return <ColorModeContext.Provider value={value}>
            <ThemeProvider theme={theme}>{children}</ThemeProvider>
        </ColorModeContext.Provider>;
}
export function useColorModeContext() {
  const ctx = useContext(ColorModeContext);
  if (!ctx) {
    throw new Error('useColorMode must be used within ColorModeProvider');
  }
  return ctx;
}